// WhyCanadaPR.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Briefcase, GraduationCap, Shield, TrendingUp } from 'lucide-react';

const WhyCanadaPR = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'Open Immigration Targets',
      description: 'Federal planning aims to admit over 500,000 new permanent residents a year, through published routes whose criteria you can check before you apply.'
    },
    {
      icon: Heart,
      title: 'Everyday Quality of Life',
      description: 'Canada sits year after year near the top of global liveability rankings, helped by strong healthcare, schooling and public services.'
    },
    {
      icon: Briefcase,
      title: 'A Broad Job Market',
      description: 'Hiring runs across a wide spread of industries, with pay that competes internationally and employment law that protects staff.'
    },
    {
      icon: GraduationCap,
      title: 'Schools and Universities',
      description: 'Children of permanent residents attend public school free of charge, and highly ranked universities are within reach.'
    },
    {
      icon: Shield,
      title: 'Public Support Systems',
      description: 'Residents and their families can draw on universal healthcare, social security and wider welfare provision.'
    },
    {
      icon: TrendingUp,
      title: 'Citizenship Later On',
      description: 'Once the residency requirement has been met, usually 3 years, a permanent resident can apply to become a Canadian citizen.'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            What Canadian Permanent Residency Gives You
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Few countries pair this much economic opportunity with this standard of living, which is why Canadian PR remains one of the most sought-after statuses anywhere.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-surface p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            <strong>Worth knowing:</strong> permanent residents hold close to the same rights as citizens. That covers healthcare, education, and the freedom to live, work or study in any part of the country.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyCanadaPR;
